import { useState } from 'react'
import type { AgendaItem, BoardMember } from '../api/client'
import { Avatar } from '../components/Avatar'
import { Prose } from '../components/Prose'

/*
  An item on the table, as a conversation (UI SPEC §6).

  The spec's card sketch stacked the note, the clarifier and the answer as three styled
  regions. That reads as a record. Rendering the same three things as a thread makes it
  read as something that happened: the owner said a thing on Tuesday, Priya asked about it
  on Wednesday, the owner answered. Attribution and time under each message is what turns a
  form into a conversation, so both are always shown and neither is ever guessed at.

  Sides are deliberate. The owner is on the right the way they are in every messaging app
  they already use; the board is on the left. Nobody has to be taught that.
*/

interface Props {
  item: AgendaItem
  board: BoardMember[]
  onAnswer: (id: string, answer: string) => Promise<void>
}

export function AgendaThread({ item, board, onAnswer }: Props) {
  const asker = board.find(m => m.id === item.clarifierMemberId) ?? null

  const answered = board.find(m => m.id === item.consultMemberId) ?? null

  /*
    Who said the thing at the top of the thread.

    Nearly always the owner: the table is where they put what is on their mind. Not on a
    system item, which is the board raising something itself. The nightly sweep files "You
    said you would X. Did it happen, did it change, or did it stop mattering?", and
    attributing that to the owner put the board's question in the owner's mouth, on the
    right, as though they had asked it. The phrase "You said you would" makes it read like
    the owner talking about themselves in the second person.
  */
  const raised = item.source === 'system'

  /*
    An item that is asking the owner something and has not been answered.

    It used to be only the clarifier: a seat reading an item and asking one question back.
    But a system item asks its question in its own text and carries no clarifier at all, so
    the board asked "did it happen?" and the owner had nowhere to type the answer. All they
    were offered was Not this week and Remove, which are both ways of refusing to answer.
  */
  const awaitingAnswer =
    !item.clarifierAnswer && (Boolean(item.clarifierQuestion) || raised)

  return (
    <div className="thread">
      {/*
        An escalated item was submitted to the agenda when the owner pressed escalate,
        which is after the consult they are reading. Timestamping the question with the
        escalation would show the board answering a minute before it was asked.
      */}
      <Message
        side={raised ? 'board' : 'owner'}
        who={raised ? asker?.displayName ?? 'The board' : 'You'}
        at={item.consultAskedAt ?? item.submittedAt}
        text={item.rawText}
        member={raised ? asker : null}
      />

      {/* An item escalated from a consult already has a reply. Shown as the message it
          was, from the seat that gave it, rather than quoted back at the owner. */}
      {item.consultAnswer && (
        <Message
          side="board"
          who={answered?.displayName ?? 'The board'}
          at={item.consultAnsweredAt ?? item.submittedAt}
          text={item.consultAnswer}
          member={answered}
        />
      )}

      {item.clarifierQuestion && (
        <Message
          side="board"
          who={asker?.displayName ?? 'The board'}
          // Older questions predate attribution and carry no asked_at. Falling back to the
          // submission time is honest about ordering without inventing a moment.
          at={item.clarifierAskedAt ?? item.submittedAt}
          text={item.clarifierQuestion}
          member={asker}
        />
      )}

      {item.clarifierAnswer && (
        <Message
          side="owner"
          who="You"
          at={item.clarifierAnsweredAt ?? item.submittedAt}
          text={item.clarifierAnswer}
        />
      )}

      {awaitingAnswer && (
        <Reply id={item.id} who={asker?.displayName.split(' ')[0] ?? 'the board'} onAnswer={onAnswer} />
      )}
    </div>
  )
}

function Message({
  side, who, at, text, member,
}: {
  side: 'owner' | 'board'
  who: string
  at: string
  text: string
  member?: BoardMember | null
}) {
  return (
    <div className={`msg msg--${side}`}>
      {side === 'board' && (
        <span className="msg__avatar">
          {member
            ? <Avatar member={member} size="sm" />
            : <span className="avatar">BS</span>}
        </span>
      )}

      <span className="msg__body">
        <span className="msg__bubble"><Prose raw={text} /></span>
        {/* §6 asks for both, under the message rather than above it. */}
        <span className="msg__by">{who} · {when(at)}</span>
      </span>
    </div>
  )
}

function Reply({
  id, who, onAnswer,
}: {
  id: string
  who: string
  onAnswer: (id: string, answer: string) => Promise<void>
}) {
  const [value, setValue] = useState('')
  const [sending, setSending] = useState(false)

  const send = async () => {
    const answer = value.trim()
    if (!answer || sending) return
    setSending(true)
    try {
      await onAnswer(id, answer)
      setValue('')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="reply">
      <input
        className="reply__box"
        value={value}
        placeholder={`Answer ${who}`}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void send() } }}
      />
      <button className="btn btn--primary" disabled={sending || !value.trim()} onClick={send}>
        {sending ? 'Sending' : 'Send'}
      </button>
    </div>
  )
}

/*
  Message-app time, not a log timestamp. Today is a clock, this week is a weekday, older is
  a date. The owner cares whether this was this morning or last Tuesday, never that it was
  2026-08-25T06:42:11Z.
*/
export function when(iso: string): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return ''

  const now = new Date()
  const clock = then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  const sameDay = then.toDateString() === now.toDateString()
  if (sameDay) return clock

  const days = Math.floor((now.getTime() - then.getTime()) / 86_400_000)
  if (days < 1) return `Yesterday ${clock}`
  if (days < 7) return `${then.toLocaleDateString(undefined, { weekday: 'short' })} ${clock}`

  return `${then.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} ${clock}`
}
